import React, { useState, useEffect } from "react";
import Select from "react-select";
import Cookies from "js-cookie";
import config from "../../config";
import styles from "../css/css_pages/ClientManage.module.css";

import {
  FiPlus,
  FiSearch,
  FiRefreshCw,
  FiEdit2,
  FiX,
  FiTrash2,
} from "react-icons/fi";


const TSOManagement = () => {
  const [tsos, setTsos] = useState([]);
  const [filteredTsos, setFilteredTsos] = useState([]);
  const [selectedTsoFilter, setSelectedTsoFilter] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);

  // TSO data
  const [currentTso, setCurrentTso] = useState(null);
  const [formData, setFormData] = useState({
    emp_no: "",
    calling_name: "",
    full_name: "",
    type: "",
    phone_number: "",
  });

  const [formErrors, setFormErrors] = useState({});
  const [updateStatus, setUpdateStatus] = useState({
    success: null,
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // TSO types for dropdown
  const tsoTypes = [
    { value: "TSO", label: "TSO" },
    { value: "TSO Online", label: "TSO Online" },
    { value: "management", label: "Management" },
  ];

  // Fetch TSOs on component mount
  useEffect(() => {
    fetchData();
  }, []);

  // Update filtered TSOs when TSOs or filters change
  useEffect(() => {
    filterTsos();
  }, [tsos, selectedTsoFilter, searchTerm]);

  const filterTsos = () => {
    let filtered = [...tsos];

    // Filter by search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (tso) =>
          tso.tso_full_name?.toLowerCase().includes(term) ||
          tso.tso_emp_no?.toLowerCase().includes(term) ||
          tso.tso_type?.toLowerCase().includes(term) ||
          tso.tso_calling_name?.toLowerCase().includes(term) ||
          tso.tso_phone?.toLowerCase().includes(term)
      );
    }

    // Filter by selected TSO type
    if (selectedTsoFilter && selectedTsoFilter.value !== "all") {
      filtered = filtered.filter(
        (tso) => tso.tso_type === selectedTsoFilter.value
      );
    }

    setFilteredTsos(filtered);
  };

  const fetchData = async () => {
    setIsLoading(true);
    setIsRefreshing(true);
    setError(null);

    try {
      const token = Cookies.get("authToken");
      if (!token) throw new Error("Authentication token not found");

      const tsosResponse = await fetch(
        `${config.API_URL}/management/tso-list`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!tsosResponse.ok) throw new Error("Failed to fetch TSOs");

      const tsosData = await tsosResponse.json();
      console.log("TSO data", tsosData);

      setTsos(Array.isArray(tsosData) ? tsosData : []);
      setFilteredTsos(Array.isArray(tsosData) ? tsosData : []);
    } catch (error) {
      console.error("Error fetching data:", error);
      setError(error.message);
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  };

  const handleCreateTso = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    setUpdateStatus({ success: null, message: "" });

    try {
      const token = Cookies.get("authToken");
      if (!token) throw new Error("Authentication token not found");

      const response = await fetch(
        `${config.API_URL}/management/create-tso`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(formData),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Failed to create TSO");
      }

      setUpdateStatus({
        success: true,
        message: "TSO created successfully!",
      });

      // Refresh TSO list
      await fetchData();

      // Close modal after short delay
      setTimeout(() => {
        resetForm();
        setShowCreateModal(false);
      }, 1500);
    } catch (error) {
      console.error("Error creating TSO:", error);
      setUpdateStatus({
        success: false,
        message: error.message || "Failed to create TSO",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateTso = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    setUpdateStatus({ success: null, message: "" });

    try {
      const token = Cookies.get("authToken");
      if (!token) throw new Error("Authentication token not found");

      const response = await fetch(
        `${config.API_URL}/management/update-tso/${currentTso.tso_id}`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(formData),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Failed to update TSO");
      }

      setUpdateStatus({
        success: true,
        message: "TSO updated successfully!",
      });

      // Refresh TSO list
      await fetchData();

      // Close modal after short delay
      setTimeout(() => {
        resetForm();
        setShowUpdateModal(false);
      }, 1500);
    } catch (error) {
      console.error("Error updating TSO:", error);
      setUpdateStatus({
        success: false,
        message: error.message || "Failed to update TSO",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteTso = async () => {
    setIsSubmitting(true);

    try {
      const token = Cookies.get("authToken");
      if (!token) throw new Error("Authentication token not found");

      const response = await fetch(
        `${config.API_URL}/management/delete-tso/${currentTso.tso_id}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || "Failed to delete TSO");
      }

      // Refresh TSO list
      await fetchData();

      // Close modal
      setShowDeleteConfirmModal(false);
    } catch (error) {
      console.error("Error deleting TSO:", error);
      alert(`Error: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.full_name?.trim()) {
      errors.full_name = "Full name is required";
    }

    if (!formData.type?.trim()) {
      errors.type = "Type is required";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const resetForm = () => {
    setFormData({
      emp_no: "",
      calling_name: "",
      full_name: "",
      type: "",
      phone_number: "",
    });
    setFormErrors({});
    setUpdateStatus({ success: null, message: "" });
  };

  const openCreateModal = () => {
    resetForm();
    setShowCreateModal(true);
  };

  const openUpdateModal = (tso) => {
    setCurrentTso(tso);
    setFormData({
      emp_no: tso.tso_emp_no || "",
      calling_name: tso.tso_calling_name || "",
      full_name: tso.tso_name || "",
      type: tso.tso_type || "",
      phone_number: tso.tso_phone || "",
    });
    setShowUpdateModal(true);
  };

  const openViewModal = (tso) => {
    setCurrentTso(tso);
    setShowViewModal(true);
  };

  const openDeleteConfirmModal = (tso) => {
    setCurrentTso(tso);
    setShowDeleteConfirmModal(true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error for this field if it exists
    if (formErrors[name]) {
      setFormErrors((prev) => ({
        ...prev,
        [name]: null,
      }));
    }
  };

  const handleTsoTypeChange = (selected) => {
    setFormData((prev) => ({
      ...prev,
      type: selected.value,
    }));

    // Clear error for this field if it exists
    if (formErrors.type) {
      setFormErrors((prev) => ({
        ...prev,
        type: null,
      }));
    }
  };

  const handleFilterChange = (selected) => {
    setSelectedTsoFilter(selected);
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const clearFilters = () => {
    setSelectedTsoFilter(null);
    setSearchTerm("");
  };

  // Loading state
  if (isLoading && !isRefreshing) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.loadingSpinner} />
        <p>Loading TSOs...</p>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className={styles.errorContainer}>
        <p className={styles.errorText}>Error: {error}</p>
        <button className={styles.retryButton} onClick={fetchData}>
          Retry
        </button>
      </div>
    );
  }

  // Main render
  return (
    <div className={styles.invoiceManagement}>
      <header className={styles.managementHeader}>
        <div className={styles.headerTitleRow}>
          <h1>TSO Management</h1>
          <button
            className={styles.refreshButton}
            disabled={isRefreshing}
            onClick={fetchData}
          >
            <FiRefreshCw className={isRefreshing ? styles.refreshing : ""} />
          </button>
        </div>

        <div className={styles.headerControls}>
          <div className={styles.controlsLeft}>
            <button className={styles.createBtn} onClick={openCreateModal}>
              <FiPlus /> Create TSO
            </button>
          </div>

          <div className={styles.controlsRight}>
            <div className={styles.searchBar}>
              <FiSearch className={styles.searchIcon} />
              <input
                type="text"
                placeholder="Search TSOs..."
                value={searchTerm}
                onChange={handleSearch}
              />
            </div>
          </div>
        </div>
      </header>

      <div className={styles.invoicesContainer}>
        <div className={styles.tableResponsiveWrapper}>
          {filteredTsos.length > 0 ? (
            <>
              <div className={styles.tableInfo}>
                Showing {filteredTsos.length} of {tsos.length} TSOs
              </div>
              <div className={styles.tableScrollWrapper}>
                <table className={styles.invoicesTable}>
                  <thead>
                    <tr>
                      <th className={styles.numberColumn}>Employee Number</th>
                      <th className={styles.clientColumn}>Full Name</th>
                      <th className={styles.typeColumn}>Type</th>
                      <th className={styles.balanceColumn}>Phone Number</th>
                      <th className={styles.actionsColumn}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredTsos.map((tso) => (
                      <tr
                        key={tso.id}
                        className={styles.clickableRow}
                        onClick={() => openViewModal(tso)} // Add this line
                        style={{ cursor: "pointer" }} // Optional: Change cursor to indicate clickable
                      >
                        <td className={styles.numberCell}>{tso.tso_emp_no || "N/A"}</td>
                        <td className={styles.clientCell}>
                          {tso.tso_name || "N/A"}
                        </td>
                        <td className={styles.typeCell}>
                          {tso.tso_type || "N/A"}
                        </td>
                        <td className={styles.balanceCell}>
                          {tso.tso_phone || "N/A"}
                        </td>
                        <td className={styles.actionsCell}>
                          <button
                            className={styles.updateBtn}
                            title="Edit TSO"
                            onClick={(e) => {
                              e.stopPropagation(); // Prevent triggering row click
                              openUpdateModal(tso);
                            }}
                          >
                            <FiEdit2 />
                          </button>
                          <button
                            className={styles.deleteBtn}
                            title="Delete TSO"
                            onClick={(e) => {
                              e.stopPropagation(); // Prevent triggering row click
                              openDeleteConfirmModal(tso);
                            }}
                          >
                            <FiTrash2 />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <div className={styles.noResults}>
              <p>No TSOs found matching your criteria.</p>
              {(selectedTsoFilter || searchTerm) && (
                <button className={styles.clearFilters} onClick={clearFilters}>
                  Clear filters
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Create TSO Modal */}
      {showCreateModal && (
        <div className={styles.modalOverlay}>
          <div className={styles.modalContent}>
            <div className={styles.modalHeader}>
              <h2>Create New TSO</h2>
              <button
                className={styles.closeButton}
                onClick={() => setShowCreateModal(false)}
              >
                <FiX />
              </button>
            </div>

            <div className={styles.modalBody}>
              <form className={styles.updateForm} onSubmit={handleCreateTso}>
                {updateStatus.message && (
                  <div
                    className={
                      updateStatus.success
                        ? styles.successMessage
                        : styles.errorMessage
                    }
                  >
                    {updateStatus.message}
                  </div>
                )}

                <div className={styles.formGroup}>
                  <label htmlFor="full_name">Full Name*</label>
                  <input
                    type="text"
                    id="full_name"
                    name="full_name"
                    value={formData.full_name}
                    onChange={handleInputChange}
                    className={
                      formErrors.full_name ? styles.errorInput : ""
                    }
                    required
                  />
                  {formErrors.full_name && (
                    <span className={styles.errorMessage}>
                      {formErrors.full_name}
                    </span>
                  )}
                </div>

                <div className={styles.formGroup}>
                  <label htmlFor="full_name">Calling Name*</label>
                  <input
                    type="text"
                    id="calling_name"
                    name="calling_name"
                    value={formData.calling_name}
                    onChange={handleInputChange}
                    className={
                      formErrors.calling_name ? styles.errorInput : ""
                    }
                    required
                  />
                  {formErrors.full_name && (
                    <span className={styles.errorMessage}>
                      {formErrors.full_name}
                    </span>
                  )}
                </div>

                <div className={styles.formGroup}>
                  <label htmlFor="type">Type*</label>
                  <Select
                    id="type"
                    name="type"
                    options={tsoTypes}
                    value={
                      tsoTypes.find(
                        (type) => type.value === formData.type
                      ) || null
                    }
                    onChange={handleTsoTypeChange}
                    styles={customSelectStyles}
                    className={
                      formErrors.type ? styles.errorInput : ""
                    }
                    placeholder="Select TSO type"
                  />
                  {formErrors.type && (
                    <span className={styles.errorMessage}>
                      {formErrors.type}
                    </span>
                  )}
                </div>

                <div className={styles.formGroup}>
                  <label htmlFor="emp_no">Employee No</label>
                  <input
                    type="text"
                    id="emp_no"
                    name="emp_no"
                    value={formData.emp_no}
                    onChange={handleInputChange}
                  />
                </div>

                <div className={styles.formGroup}>
                  <label htmlFor="phone_number">Phone Number</label>
                  <input
                    type="text"
                    id="phone_number"
                    name="phone_number"
                    value={formData.phone_number}
                    onChange={handleInputChange}
                  />
                </div>

                <div className={styles.formActions}>
                  <button
                    type="button"
                    className={styles.cancelButton}
                    onClick={() => setShowCreateModal(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className={styles.submitButton}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Creating..." : "Create TSO"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Update TSO Modal */}
      {showUpdateModal && currentTso && (
        <div className={styles.modalOverlay}>
          <div className={styles.modalContent}>
            <div className={styles.modalHeader}>
              <h2>Update TSO ({currentTso.tso_calling_name})</h2>
              <button
                className={styles.closeButton}
                onClick={() => setShowUpdateModal(false)}
              >
                <FiX />
              </button>
            </div>

            <div className={styles.modalBody}>
              <form className={styles.updateForm} onSubmit={handleUpdateTso}>
                {updateStatus.message && (
                  <div
                    className={
                      updateStatus.success
                        ? styles.successMessage
                        : styles.errorMessage
                    }
                  >
                    {updateStatus.message}
                  </div>
                )}

                <div className={styles.formGroup}>
                  <label htmlFor="full_name">Full Name*</label>
                  <input
                    type="text"
                    id="full_name"
                    name="full_name"
                    value={formData.full_name}
                    onChange={handleInputChange}
                    className={
                      formErrors.full_name ? styles.errorInput : ""
                    }
                    required
                  />
                  {formErrors.full_name && (
                    <span className={styles.errorMessage}>
                      {formErrors.full_name}
                    </span>
                  )}
                </div>

                <div className={styles.formGroup}>
                  <label htmlFor="type">Type*</label>
                  <input
                    type="text"
                    id="full_name"
                    name="full_name"
                    value={formData.type}
                    onChange={handleInputChange}
                    className={
                      formErrors.full_name ? styles.errorInput : ""
                    }
                    disabled
                  />
                  {formErrors.type && (
                    <span className={styles.errorMessage}>
                      {formErrors.type}
                    </span>
                  )}
                </div>

                <div className={styles.formGroup}>
                  <label htmlFor="emp_no">Employee No</label>
                  <input
                    type="text"
                    id="emp_no"
                    name="emp_no"
                    value={formData.emp_no}
                    onChange={handleInputChange}
                  />
                </div>

                <div className={styles.formGroup}>
                  <label htmlFor="phone_number">Phone Number</label>
                  <input
                    type="text"
                    id="phone_number"
                    name="phone_number"
                    value={formData.phone_number}
                    onChange={handleInputChange}
                  />
                </div>

                <div className={styles.formActions}>
                  <button
                    type="button"
                    className={styles.cancelButton}
                    onClick={() => setShowUpdateModal(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className={styles.submitButton}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Updating..." : "Update TSO"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* View TSO Modal */}
      {showViewModal && currentTso && (
        <div className={styles.modalOverlay}>
          <div className={styles.largeModalContent}>
            <div className={styles.modalHeader}>
              <h2>TSO Details ({currentTso.tso_calling_name})</h2>
              <button
                className={styles.closeButton}
                onClick={() => setShowViewModal(false)}
              >
                <FiX />
              </button>
            </div>

            <div className={styles.modalBody}>
              <div className={styles.invoiceSummary}>
                <div className={styles.summaryRow}>
                  <div className={styles.summaryItem}>
                    <span className={styles.summaryLabel}>Full Name:</span>
                    <span className={styles.summaryValue}>
                      {currentTso.tso_name || "N/A"}
                    </span>
                  </div>
                  <div className={styles.summaryItem}>
                    <span className={styles.summaryLabel}>Type:</span>
                    <span className={styles.summaryValue}>
                      {currentTso.tso_type || "N/A"}
                    </span>
                  </div>
                </div>
                <div className={styles.summaryRow}>
                  <div className={styles.summaryItem}>
                    <span className={styles.summaryLabel}>Employee No:</span>
                    <span className={styles.summaryValue}>
                      {currentTso.tso_emp_no || "N/A"}
                    </span>
                  </div>
                  <div className={styles.summaryItem}>
                    <span className={styles.summaryLabel}>Phone Number:</span>
                    <span className={styles.summaryValue}>
                      {currentTso.tso_phone || "N/A"}
                    </span>
                  </div>
                </div>
              </div>

              <div className={styles.modalActions}>
                <button
                  className={styles.editButton}
                  onClick={() => {
                    setShowViewModal(false);
                    openUpdateModal(currentTso);
                  }}
                >
                  <FiEdit2 /> Edit TSO
                </button>
                <button
                  className={styles.deleteButton}
                  onClick={() => {
                    setShowViewModal(false);
                    openDeleteConfirmModal(currentTso);
                  }}
                >
                  <FiTrash2 /> Delete TSO
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirmModal && currentTso && (
        <div className={styles.modalOverlay}>
          <div className={styles.confirmationModal}>
            <div className={styles.modalHeader}>
              <h2>Confirm Deletion</h2>
              <button
                className={styles.closeButton}
                onClick={() => setShowDeleteConfirmModal(false)}
              >
                <FiX />
              </button>
            </div>

            <div className={styles.modalBody}>
              <div className={styles.confirmationContent}>
                <p className={styles.confirmationText}>
                  Are you sure you want to delete TSO:{" "}
                  <strong>{currentTso.tso_name}</strong> (ID:{" "}
                  {currentTso.tso_emp_no})?
                </p>
                <p className={styles.confirmationWarning}>
                  This action cannot be undone. All associated data will be
                  permanently removed.
                </p>
              </div>

              <div className={styles.confirmationActions}>
                <button
                  className={styles.cancelButton}
                  onClick={() => setShowDeleteConfirmModal(false)}
                >
                  Cancel
                </button>
                <button
                  className={styles.dangerButton}
                  onClick={handleDeleteTso}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Deleting..." : "Delete TSO"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Custom styles for react-select component
const customSelectStyles = {
  control: (provided) => ({
    ...provided,
    minHeight: "40px",
    border: "1px solid #e2e8f0",
    borderRadius: "8px",
    boxShadow: "none",
    "&:hover": {
      borderColor: "#94a3b8",
    },
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected ? "#e0e7ff" : "white",
    color: state.isSelected ? "#1e40af" : "#1e293b",
    "&:hover": {
      backgroundColor: "#f1f5f9",
      color: "#1e40af",
    },
  }),
  menu: (provided) => ({
    ...provided,
    borderRadius: "8px",
    border: "1px solid #e2e8f0",
    boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
  }),
  indicatorSeparator: () => ({
    display: "none",
  }),
  dropdownIndicator: (provided) => ({
    ...provided,
    color: "#94a3b8",
    "&:hover": {
      color: "#64748b",
    },
  }),
};

export default TSOManagement;